/**
 * @fileoverview Shared email-domain classification for form + send-time guards.
 * @description Single source of truth for "is this address worth accepting?" so
 * the request-access wizard, the email-capture hook, and the Postmark send
 * action all agree. Pure (no env, no I/O) so it imports cleanly into client
 * components, server actions, and unit tests alike.
 *
 * Two independent classifications, intentionally separate:
 *
 *  - {@link isBlockedEmailDomain} — placeholder / example / disposable domains
 *    that are NEVER a real lead. A submission from one of these hard-bounces
 *    (the `managementheadquarters50@email.com` → HardBounce incident that
 *    motivated this module), wastes a Postmark send, pollutes deliverability
 *    stats, and seeds the CRM with junk contacts. These are rejected at the
 *    form AND refused at send time.
 *  - {@link isPersonalEmailDomain} — real consumer webmail (gmail, yahoo, …).
 *    These ARE deliverable; the request-access wizard rejects them only because
 *    it wants a *work* email, but the email-capture / waitlist flow happily
 *    accepts them. So this is advisory per-caller, never a send-time block.
 *
 * @module lib/validation/email-domains
 */

/**
 * Extract the lowercased domain (the part after the last `@`) from an address,
 * or `null` when there isn't one. Trims surrounding whitespace first — Postmark
 * payloads and pasted form values frequently arrive padded
 * (`" managementheadquarters50@email.com "` in the bounce log).
 */
export function getEmailDomain(email: string): string | null {
  const normalized = email.trim().toLowerCase()
  const at = normalized.lastIndexOf("@")
  if (at === -1 || at === normalized.length - 1) return null
  return normalized.slice(at + 1)
}

function getEmailLocalPart(email: string): string | null {
  const normalized = email.trim().toLowerCase()
  const at = normalized.lastIndexOf("@")
  if (at <= 0) return null
  return normalized.slice(0, at)
}

/**
 * Placeholder / example domains that are never a legitimate lead. Matched by
 * **exact apex only** — a subdomain like `mail.example.com` can be a real
 * internal host, so we don't suffix-match these.
 *
 * Curated to avoid false-positives on real businesses: form-placeholder
 * strings plus generic non-client filler. Notably absent is real consumer
 * webmail (e.g. `mail.com`) — those are handled as PERSONAL below (rejected by
 * the B2B request-access flow, accepted by the consumer waitlist), not
 * hard-blocked, because a genuine consumer signup can legitimately use one.
 * Disposable/throwaway providers are a SEPARATE set below (suffix-matched).
 *
 * Caveat: `email.com` IS a real free-webmail provider (the mail.com family), so
 * blocking it has a small false-positive cost for genuine *consumer* signups on
 * the email-capture flow. We block it anyway because (a) its overwhelming usage
 * is as "type your email here" placeholder filler, (b) it was the actual
 * HardBounce domain that motivated this module, and (c) the request-access flow
 * rejects all personal webmail regardless. The tradeoff is deliberate.
 */
export const PLACEHOLDER_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // RFC 2606 reserved / documentation domains + the generic throwaways that
  // mirror the form's own placeholder copy.
  "email.com",
  "example.com",
  "example.org",
  "example.net",
  "example.edu",
  "test.com",
  "test.net",
  "test.org",
  "sample.com",
  "company.com", // generic placeholder — not a real client domain (per product owner)
  "yourcompany.com", // literal placeholder in the website field copy
  "yourdomain.com",
  "fake.com",
  "fakeemail.com",
  "noemail.com",
  "nomail.com",
  "no-email.com",
  "none.com",
  "invalid.com",
])

/**
 * Known disposable / throwaway inbox PROVIDERS (hard-bounce or expire fast, and
 * signal a non-serious submission). Matched on the apex **and any subdomain** —
 * providers like Mailinator hand out `<anything>.mailinator.com`, so an
 * apex-only block is a trivial bypass (`user@x.mailinator.com`).
 */
export const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "guerrillamail.info",
  "guerrillamail.net",
  "guerrillamail.org",
  "guerrillamail.biz",
  "guerrillamailblock.com",
  "sharklasers.com",
  "grr.la",
  "10minutemail.com",
  "10minutemail.net",
  "20minutemail.com",
  "tempmail.com",
  "temp-mail.org",
  "temp-mail.io",
  "tempmail.net",
  "tempmailo.com",
  "tempr.email",
  "tempail.com",
  "1secmail.com",
  "1secmail.org",
  "1secmail.net",
  "throwaway.email",
  "trashmail.com",
  "trashmail.de",
  "trash-mail.com",
  "wegwerfmail.de",
  "yopmail.com",
  "yopmail.net",
  "yopmail.fr",
  "getnada.com",
  "nada.email",
  "maildrop.cc",
  "dispostable.com",
  "mailnesia.com",
  "fakeinbox.com",
  "fakemailgenerator.com",
  "mintemail.com",
  "mohmal.com",
  "spam4.me",
  "spambog.com",
  "spam.la",
  "discard.email",
  "emailondeck.com",
  "getairmail.com",
  "33mail.com",
  "mailcatch.com",
  "dropmail.me",
  "moakt.com",
  "inboxkitten.com",
  "mailpoof.com",
  "jetable.org",
  "throwawaymail.com",
  "tmpmail.org",
  "mailto.plus",
  // Domains observed in July 2026 spam submissions that accepted mail then
  // quickly opened/clicked/bounced, damaging Postmark reputation without
  // representing real leads. Keep these in the disposable set so all entry
  // points (forms, CRM upsert, send-time guard) refuse them consistently.
  "noexpire.top",
  "vtmpj.com",
])

/**
 * Union of every domain we hard-block (placeholder ∪ disposable). Exposed mainly
 * for the list-hygiene test (must stay disjoint from {@link PERSONAL_EMAIL_DOMAINS}).
 */
export const BLOCKED_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  ...PLACEHOLDER_EMAIL_DOMAINS,
  ...DISPOSABLE_EMAIL_DOMAINS,
])

/**
 * Real consumer-webmail domains. Deliverable mail — used by the request-access
 * wizard to nudge applicants toward a *work* address, NOT a hard block on
 * deliverability. Consolidated here so the wizard and the email-capture hook
 * share one list instead of two drifting copies.
 */
export const PERSONAL_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "mail.com",
  "gmx.com",
  "gmx.net",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "zoho.com",
  "yandex.com",
])

const SUSPICIOUS_THROWAWAY_TLDS: ReadonlySet<string> = new Set([
  "top",
  "xyz",
  "icu",
  "cyou",
])

/**
 * `true` when the address's domain should never be accepted as a lead or
 * emailed: an exact placeholder/example apex, or a disposable provider apex OR
 * any subdomain of one.
 */
export function isBlockedEmailDomain(email: string): boolean {
  const domain = getEmailDomain(email)
  if (domain === null) return false
  if (PLACEHOLDER_EMAIL_DOMAINS.has(domain)) return true
  for (const disposable of DISPOSABLE_EMAIL_DOMAINS) {
    if (domain === disposable || domain.endsWith(`.${disposable}`)) return true
  }
  return false
}

/**
 * Heuristic throwaway/spambot lead detection beyond known blocklists.
 *
 * **Lead-ingress only.** This is a *heuristic* — deliberately not wired into the
 * shared {@link isBlockedEmailDomain} send-time guard, because a false positive
 * on the send path would silently drop an existing account's magic link or
 * purchase receipt (#525). Callers that gate *new leads* (the request-access
 * schema, the email-capture hook, the CRM upsert) use this; `sendEmail` keeps
 * using the deterministic exact/disposable blocklist.
 *
 * It requires a **disposable-domain signal** *and* a corroborating signal
 * (generated word+number local part OR a throwaway TLD). We never block on a
 * single weak signal:
 *
 *  - We do NOT block a long corporate mailbox by local part alone
 *    (`christopherjohnson@acme.com`), a consonant-heavy brand apex alone
 *    (`christopherjohnson@crypt.co`, `alexandermorgan@rhythm.com`), or a short
 *    domain alone (`figma.com`). The earlier consonant-only-apex + long-local
 *    leg was removed after it false-positived on exactly these real shapes
 *    (#525 r3530661738, r3530673377) — pure high-entropy domains that are
 *    genuinely observed as spam belong in the exact {@link DISPOSABLE_EMAIL_DOMAINS}
 *    blocklist (deterministic, zero false positives), not a guessing heuristic.
 *  - Disposable-inbox wording is detected across ALL domain labels (not one
 *    positional label), so it fires wherever the wording lives — a plain apex
 *    (`disposablemail.top`) or a label hidden by a multi-label public suffix
 *    (`noexpiremail.co.uk`) — without needing a full public-suffix list, and
 *    without flagging a legitimate `mail.` subdomain (whose bare `mail` label
 *    carries no inbox-word compound) (#525 r3530658641, r3530673358, r3531039108).
 *
 * Observed spam that motivated this (`lucky_tiger46@noexpire.top`,
 * `kdlaewlisjwerlfbpx@vtmpj.com`) is already caught deterministically by the
 * exact disposable blocklist above; this heuristic generalizes to close
 * lookalikes such as `summer_bear42@noexpiremail.top`.
 */
export function isLikelyLeadSpamEmail(email: string): boolean {
  if (isBlockedEmailDomain(email)) return true

  const domain = getEmailDomain(email)
  const localPart = getEmailLocalPart(email)
  if (!domain || !localPart) return false

  const labels = domain.split(".")
  const tld = labels.at(-1) ?? ""

  // Scan EVERY label for disposable-inbox wording rather than picking one
  // positional "apex" label. Positional lookup can't be right for both plain
  // apexes and multi-label public suffixes without a full PSL — the second-to-
  // last label is `co` for `noexpiremail.co.uk`, hiding the disposable wording
  // in the third-to-last label (#525 r3531039108). Scanning all labels is safe
  // here precisely because the marker requires a disposable descriptor IMMEDIATELY
  // followed by an inbox word: a bare subdomain (`mail.acme.top` → `mail`) or a
  // product apex (`disposablepackaging`) matches nothing, so this no longer
  // reintroduces the subdomain false positive the positional lookup was guarding
  // against (#525 r3530658641).
  const domainAdvertisesDisposableUse = labels.some(apexAdvertisesDisposableUse)

  // Score the DELIVERABLE local part: strip the `+tag` sub-address so a generated
  // local can't dodge the check by appending a plus tag — `summer_bear42+x`
  // delivers to the same mailbox as `summer_bear42` (#525 r3531184295).
  const deliverableLocal = localPart.replace(/\+.*$/, "")
  const generatedWordNumberLocal =
    /^[a-z]+(?:[._-][a-z]+)*\d{2,}$/.test(deliverableLocal) &&
    deliverableLocal.length >= 8

  const suspiciousThrowawayTld = SUSPICIOUS_THROWAWAY_TLDS.has(tld)

  return (
    domainAdvertisesDisposableUse &&
    (generatedWordNumberLocal || suspiciousThrowawayTld)
  )
}

/**
 * Disposable-inbox wording: a throwaway DESCRIPTOR immediately followed by an
 * INBOX word (`mail`/`email`/`inbox`/`box`) anywhere in the apex's letters.
 * This catches lookalike compounds and affixed forms — `tempmail`,
 * `throwawaymail`, `disposablemail`, `burnermail`, and the `noexpiremail`
 * lookalike (via `expire`+`mail`) — while requiring the inbox suffix so a
 * business that merely describes a disposable PRODUCT is NOT mistaken for a
 * throwaway email provider: `disposablepackaging.com`, `burnerphones.com`, and
 * `template.com` do not match (#525 r3530908869). Bare `mail.com` is excluded too — no
 * descriptor precedes `mail` (#525 r3530673372).
 */
const DISPOSABLE_APEX_PATTERN =
  /(?:temp|throwaway|throw|trash|discard|expire|burner|burn|fake|spam|junk|disposable|dispose)(?:mail|email|inbox|box)/

/**
 * Known disposable-provider word stems whose recognizable form survives digit
 * stripping — `10minutemail`/`20minutemail` → `minutemail`, `1secmail` →
 * `secmail`. These providers' `.com` apexes are on the exact blocklist, but a
 * `.top`/`.xyz` lookalike (`10minutemail.top`) isn't, so the heuristic must still
 * recognize the stem (#525 r3531184289). Each is a specific compound unlikely to
 * appear inside an ordinary brand apex.
 */
const DISPOSABLE_PROVIDER_STEMS: readonly string[] = ["minutemail", "secmail"]

/**
 * `true` when the apex's letters read as disposable-inbox wording — either the
 * descriptor+inbox-word pattern or a known disposable-provider stem. Operates on
 * a single domain label; digits/separators are stripped first.
 */
function apexAdvertisesDisposableUse(apex: string): boolean {
  const letters = apex.replace(/[^a-z]/g, "")
  return (
    DISPOSABLE_APEX_PATTERN.test(letters) ||
    DISPOSABLE_PROVIDER_STEMS.some((stem) => letters.includes(stem))
  )
}

/**
 * `true` when the address's domain is consumer webmail (gmail, yahoo, …).
 * Advisory — callers that require a work email reject these; others accept them.
 */
export function isPersonalEmailDomain(email: string): boolean {
  const domain = getEmailDomain(email)
  return domain !== null && PERSONAL_EMAIL_DOMAINS.has(domain)
}
